'use client';

import { useContext, useId, type FC } from 'react';
import styles from './Radio.module.css';
import type { RadioProps } from './Radio.types';
import { Pill, Text, Flex, InlineUnsafe } from '@/_components';
import { FirmSettingsContext } from '@/_contexts/FirmSettingsProvider';
import { getClasses } from '@/_utils/classnames';

export const Radio: FC<RadioProps> = ({
  onChange,
  isLabelHidden,
  isDisabled,
  helperText,
  name,
  pillText,
  value,
  selectedValue,
  label,
  withGlossary,
}) => {
  const { glossaries } = useContext(FirmSettingsContext);

  const radioId: string = useId();

  const isChecked: boolean =
    !!value && value?.toString() === selectedValue?.toString();

  const radioClasses: string = getClasses(styles.radio, {
    [styles.radioDisabled]: isDisabled,
    [styles.radioChecked]: isChecked,
  });

  return (
    <Flex isYCentered={true} className={radioClasses}>
      <input
        name={name}
        type="radio"
        id={radioId}
        onChange={onChange}
        disabled={!!isDisabled}
        checked={isChecked}
        value={value}
      />
      <label htmlFor={radioId} className={styles.radioLabel}>
        {!isLabelHidden && (
          <Text isYCentered={true} type="div">
            <InlineUnsafe
              text={label}
              glossaries={withGlossary ? glossaries : undefined}
            />
          </Text>
        )}
        {helperText && (
          <Text color="secondary" type="tiny">
            {helperText}
          </Text>
        )}
      </label>
      {pillText && (
        <Pill
          label={pillText}
          intent="secondary"
          isRound={true}
          className={styles.radioPill}
        />
      )}
    </Flex>
  );
};
